import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import type { ToolSlotResponse, ToolStatus } from '@bridgewerk/shared';
import { Badge } from '../../../components/ui/Badge.js';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { useToolLife } from '../hooks/useToolLife.js';
import { AssignToolModal } from './AssignToolModal.js';

export interface ToolLifePanelProps {
  readonly shopId: string | null;
  readonly machineId: string | null;
}

const STATUS_LABEL: Record<ToolStatus, string> = {
  change: '⚠ Change',
  order: 'Order now',
  watch: 'Watch',
  good: 'Good',
};

const STATUS_TONE: Record<ToolStatus, 'critical' | 'warning' | 'info' | 'opportunity'> = {
  change: 'critical',
  order: 'warning',
  watch: 'info',
  good: 'opportunity',
};

const STATUS_COLOR: Record<ToolStatus, string> = {
  change: 'var(--color-red)',
  order: 'var(--color-orange)',
  watch: 'var(--color-yellow)',
  good: 'var(--color-green)',
};

function toolLabel(toolNumber: number): string {
  return `T${String(toolNumber).padStart(2, '0')}`;
}

export function ToolLifePanel({ shopId, machineId }: ToolLifePanelProps): JSX.Element {
  const { data, isLoading, isError, refetch } = useToolLife(shopId, machineId);
  const [assigning, setAssigning] = useState<ToolSlotResponse | null>(null);
  const canAssign = Boolean(shopId && machineId);

  return (
    <section className="rounded-md border border-border bg-card overflow-hidden">
      <header className="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-text-dim">
          Tool Life AI — Active Tracking
        </h2>
        <span className="text-[10px] font-mono text-accent">WERK·OS</span>
      </header>

      {isLoading && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim">
              <th className="px-4 py-2 font-normal">Machine</th>
              <th className="px-4 py-2 font-normal">Tool</th>
              <th className="px-4 py-2 font-normal">Life</th>
              <th className="px-4 py-2 font-normal text-right">%</th>
              <th className="px-4 py-2 font-normal text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {[0, 1, 2].map((i) => (
              <tr key={i} className="border-t border-border">
                <td className="px-4 py-2" colSpan={5}>
                  <Skeleton className="h-4 w-full" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!isLoading && isError && (
        <div className="px-4 py-6 text-center text-xs text-text-dim font-mono">
          <p>Failed to load tool data.</p>
          <button
            onClick={refetch}
            className="mt-2 text-accent underline"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && data && data.tools.length === 0 && (
        <div className="px-4 py-6 text-center text-xs text-text-dim font-mono">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="mx-auto mb-2 h-5 w-5 opacity-40"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l5.653-4.655m5.8-8.214l4.234.693a.72.72 0 01.35 1.214l-2.212 2.211a.72.72 0 01-.637.198l-4.234-.692"
            />
          </svg>
          <p>{data.message ?? 'No tool data available'}</p>
        </div>
      )}

      {!isLoading && !isError && data && data.tools.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-mono uppercase tracking-[0.2em] text-text-dim">
              <th className="px-4 py-2 font-normal">Machine</th>
              <th className="px-4 py-2 font-normal">Tool</th>
              <th className="px-4 py-2 font-normal">Life</th>
              <th className="px-4 py-2 font-normal text-right">%</th>
              <th className="px-4 py-2 font-normal text-right">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.tools.map((tool) => (
              <tr
                key={tool.tool_number}
                className={[
                  'border-t border-border',
                  canAssign ? 'cursor-pointer hover:bg-card/40 transition-colors' : '',
                ].join(' ')}
                onClick={canAssign ? () => setAssigning(tool) : undefined}
                title={canAssign ? 'Click to assign a tool from the library' : undefined}
              >
                <td className="px-4 py-2 font-mono text-xs">{machineId}</td>
                <td className="px-4 py-2 font-mono text-xs">{toolLabel(tool.tool_number)}</td>
                <td className="px-4 py-2">
                  <div
                    className="h-1.5 rounded-full"
                    style={{ backgroundColor: 'rgba(255,255,255,0.04)' }}
                  >
                    <div
                      className="h-full rounded-full transition-[width] duration-700"
                      style={{
                        width: `${tool.life_percent}%`,
                        backgroundColor: STATUS_COLOR[tool.status],
                      }}
                    />
                  </div>
                </td>
                <td className="px-4 py-2 text-right font-mono text-xs">{tool.life_percent}%</td>
                <td className="px-4 py-2 text-right">
                  <Badge tone={STATUS_TONE[tool.status]}>{STATUS_LABEL[tool.status]}</Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <p className="px-4 py-2 text-[10px] font-mono text-muted border-t border-border">
        {data?.retrieved_at
          ? `Updated ${formatDistanceToNow(new Date(data.retrieved_at), { addSuffix: true })}`
          : 'Awaiting data'}
      </p>

      {assigning && shopId && machineId && (
        <AssignToolModal
          shopId={shopId}
          machineId={machineId}
          slot={assigning}
          onClose={() => setAssigning(null)}
        />
      )}
    </section>
  );
}
